import React, { useMemo, useState, useEffect, useRef } from "react";
import {
	FaArrowRight,
	FaExchangeAlt,
	FaInfoCircle,
	FaExclamationTriangle,
} from "react-icons/fa";
import { CART_KEY } from "../utils/constants";

import Calendar from "../components/shipping/Calendar";
import PackageTypePicker from "../components/shipping/PackageTypePicker";
import WeightBlock from "../components/shipping/WeightBlock";
import DimensionsBlock from "../components/shipping/DimensionsBlock";
import DocumentsBlock from "../components/shipping/DocumentsBlock";
import HatsBlock from "../components/shipping/HatsBlock";
import { Link } from "react-router-dom";
import {
	formatDateToDDMMYYYY,
	formatOptionalEUR,
	formatQuantityLabel,
} from "../utils/formatters";
import { useShippingData } from "../hooks/useShippingData";
import { getBilledWeight, calcPrice } from "../utils/shipmentPricing";

export default function Shipment({ variant = "default" }) {
	// =========================
	// Local state
	// =========================
	// Route / selection
	const [fromCountry, setFromCountry] = useState("ID");
	const [toCountry, setToCountry] = useState("");

	const [selectedPackageTypeId, setSelectedPackageTypeId] = useState("");

	// Shipment input
	const [weight, setWeight] = useState("");
	const [lengthCm, setLengthCm] = useState("");
	const [widthCm, setWidthCm] = useState("");
	const [heightCm, setHeightCm] = useState("");
	const [dimensionAccepted, setDimensionAccepted] = useState(true);

	const [documentPages, setDocumentPages] = useState("");
	const [hatQuantity, setHatQuantity] = useState("");
	const [shipmentDate, setShipmentDate] = useState("");

	// UI flow / feedback
	// 0: package, 1: weight/pages, 2: dimensions, 3: shipment date
	const [progressStep, setProgressStep] = useState(0);
	const [recModalOpen, setRecModalOpen] = useState(false);

	const [cartItems, setCartItems] = useState([]);
	const [feedbackItem, setFeedbackItem] = useState(null);
	const [feedbackVisible, setFeedbackVisible] = useState(false);

	// =========================================================
	// Refs
	// =========================================================
	const feedbackTimeoutRef = useRef(null);
	const shipmentRef = useRef(null);
	const dimsRef = useRef(null);

	// =========================================================
	// Data
	// =========================================================
	const { data } = useShippingData();

	const countries = useMemo(() => data?.COUNTRIES ?? [], [data]);
	const packageTypes = useMemo(() => data?.PACKAGE_TYPES ?? [], [data]);
	const sizePresets = useMemo(() => data?.SIZE_PRESETS ?? [], [data]);

	// =========================================================
	// Initial cart load
	// =========================================================
	useEffect(() => {
		try {
			const saved = localStorage.getItem(CART_KEY);
			if (saved) setCartItems(JSON.parse(saved));
		} catch {
			// ignore
		}
	}, []);

	// =========================================================
	// Route / country labels
	// =========================================================
	const canShowShipment = useMemo(() => {
		return !!fromCountry && !!toCountry && fromCountry !== toCountry;
	}, [fromCountry, toCountry]);

	// "ID_DE" or "DE_ID"
	const routeKey = useMemo(() => {
		if (!canShowShipment) return "";
		return `${fromCountry}_${toCountry}`;
	}, [canShowShipment, fromCountry, toCountry]);

	const fromID = fromCountry === "ID";
	const toID = toCountry === "ID";

	const fromCountryItem = useMemo(
		() => countries.find((c) => c.id === fromCountry) ?? null,
		[countries, fromCountry],
	);

	const toCountryItem = useMemo(
		() => countries.find((c) => c.id === toCountry) ?? null,
		[countries, toCountry],
	);

	const fromLabel = fromCountryItem?.name || "";
	const toLabel = toCountryItem?.name || "";

	// =========================================================
	// Selected package type
	// =========================================================
	const selectedPackageType = useMemo(() => {
		return packageTypes.find((p) => p.id === selectedPackageTypeId) ?? null;
	}, [selectedPackageTypeId, packageTypes]);

	const isVolume = selectedPackageType?.pricing.type === "perKg";
	const isDocument = selectedPackageTypeId === "documents";
	const isHat = selectedPackageTypeId === "hat";
	const needsDimensions = ["standard", "pkg_1_vol", "pkg_1_super"].includes(
		selectedPackageTypeId,
	);
	const needsWeight = !["hat", "documents"].includes(selectedPackageTypeId);

	// =========================================================
	// Weights / dimensions
	// =========================================================
	// billed weight for pricing + summary display (volume-like rounds to full kg)
	const billedWeight = useMemo(() => {
		return getBilledWeight(weight, isVolume);
	}, [weight, isVolume]);

	const roundedWeight = billedWeight;
	const willBeRounded = weight && Number(weight) !== roundedWeight;

	const dims = useMemo(() => {
		const l = Number(lengthCm);
		const w = Number(widthCm);
		const h = Number(heightCm);
		return {
			l: Number.isFinite(l) ? l : 0,
			w: Number.isFinite(w) ? w : 0,
			h: Number.isFinite(h) ? h : 0,
		};
	}, [lengthCm, widthCm, heightCm]);

	// volumetric weight in kg (cm³ / 6000)
	const volumetricWeight = useMemo(() => {
		const l = Number(lengthCm);
		const w = Number(widthCm);
		const h = Number(heightCm);

		if (![l, w, h].every((x) => Number.isFinite(x) && x > 0)) return 0;

		return (l * w * h) / 6000;
	}, [lengthCm, widthCm, heightCm]);

	// =========================================================
	// Recommendation logic
	// =========================================================
	const volumeDifferenceKg = useMemo(() => {
		const actualWeight = Number(weight);

		if (!Number.isFinite(actualWeight) || actualWeight <= 0) return 0;
		if (!Number.isFinite(volumetricWeight) || volumetricWeight <= 0)
			return 0;

		return volumetricWeight - actualWeight;
	}, [weight, volumetricWeight]);

	const recommendedPackageTypeId = useMemo(() => {
		if (volumeDifferenceKg <= 0) return "";
		if (!selectedPackageTypeId) return "";

		// 0 kg < volumetric weight - real weight <= 2 kg
		// If user selected Standard, recommend Volume.
		if (volumeDifferenceKg <= 2) {
			if (selectedPackageTypeId === "standard") {
				return "pkg_1_vol";
			}

			return "";
		}

		// volumetric weight - real weight > 2 kg
		// Recommend Super Volume unless already selected.
		if (volumeDifferenceKg > 2) {
			if (selectedPackageTypeId !== "pkg_1_super") {
				return "pkg_1_super";
			}

			return "";
		}

		return "";
	}, [selectedPackageTypeId, volumeDifferenceKg]);

	const recommendedLabel = useMemo(() => {
		if (!recommendedPackageTypeId) return "";
		return (
			packageTypes.find((p) => p.id === recommendedPackageTypeId)
				?.label ?? recommendedPackageTypeId
		);
	}, [recommendedPackageTypeId, packageTypes]);

	// =========================================================
	// Pricing
	// =========================================================
	const priceResult = useMemo(() => {
		return calcPrice({
			selectedPackageType,
			billedWeight: roundedWeight,
			documentPages,
			hatQuantity,
		});
	}, [selectedPackageType, roundedWeight, documentPages, hatQuantity]);

	const roundedText = useMemo(() => {
		if (!weight) return "";
		return `${Number(weight).toFixed(1)} kg → billed as ${roundedWeight.toFixed(1)} kg`;
	}, [weight, roundedWeight]);

	const priceLabel = formatOptionalEUR(priceResult.total);

	// =========================================================
	// Use Effects
	// =========================================================
	// Dimension acceptance
	useEffect(() => {
		if (!needsWeight || !needsDimensions || !recommendedPackageTypeId) {
			setDimensionAccepted(true);
			return;
		}
		setDimensionAccepted(false);
	}, [needsWeight, needsDimensions, recommendedPackageTypeId]);

	// Recommendation modal
	useEffect(() => {
		if (!needsWeight || !needsDimensions || !recommendedPackageTypeId)
			return;

		// show modal only when user has entered dims + weight
		const hasDims =
			Number(lengthCm) > 0 && Number(widthCm) > 0 && Number(heightCm) > 0;
		const hasWeight = Number(weight) > 0;

		if (!hasDims || !hasWeight) return;

		// show modal only if current selection differs OR nothing selected yet
		if (
			!selectedPackageTypeId ||
			selectedPackageTypeId !== recommendedPackageTypeId
		) {
			setRecModalOpen(true);
		}
	}, [
		needsWeight,
		needsDimensions,
		recommendedPackageTypeId,
		selectedPackageTypeId,
		lengthCm,
		widthCm,
		heightCm,
		weight,
	]);

	// Progressive reveal
	useEffect(() => {
		if (selectedPackageType && progressStep < 1) {
			setProgressStep(1);
		}
	}, [selectedPackageType, progressStep]);

	useEffect(() => {
		if (progressStep < 1) return;

		const okWeight = needsWeight && roundedWeight > 0;
		const okPages = isDocument && Number(documentPages) > 0;
		const okHatQty = isHat && Number(hatQuantity) > 0;

		if ((okWeight || okPages || okHatQty) && progressStep < 2) {
			setProgressStep(2);
		}
	}, [
		progressStep,
		needsWeight,
		isDocument,
		isHat,
		roundedWeight,
		documentPages,
		hatQuantity,
	]);

	useEffect(() => {
		if (progressStep < 2) return;
		if (!needsWeight || !needsDimensions) return;

		const hasDims =
			Number(lengthCm) > 0 && Number(widthCm) > 0 && Number(heightCm) > 0;
		if (hasDims && progressStep < 3) {
			setProgressStep(3);
		}
	}, [
		progressStep,
		needsWeight,
		needsDimensions,
		lengthCm,
		widthCm,
		heightCm,
	]);

	// =========================================================
	// Validation
	// =========================================================
	const canAddToCart = useMemo(() => {
		if (!canShowShipment || !selectedPackageType || !shipmentDate)
			return false;

		if (isDocument) {
			const pages = Number(documentPages);
			return pages > 0 && pages <= 100;
		}
		if (isHat) {
			const qty = Number(hatQuantity);
			return qty > 0;
		}

		if (roundedWeight <= 0) return false;

		if (needsDimensions) {
			if (
				Number(lengthCm) <= 0 ||
				Number(widthCm) <= 0 ||
				Number(heightCm) <= 0
			) {
				return false;
			}

			if (!dimensionAccepted) return false;
		}

		return true;
	}, [
		canShowShipment,
		selectedPackageType,
		isDocument,
		isHat,
		documentPages,
		hatQuantity,
		roundedWeight,
		needsDimensions,
		lengthCm,
		widthCm,
		heightCm,
		shipmentDate,
		dimensionAccepted,
	]);

	// =========================================================
	// Handlers
	// =========================================================
	const handleSwap = () => {
		if (!toCountry) return;
		setFromCountry(toCountry);
		setToCountry(fromCountry);
	};

	const handleWeightChange = (e) => {
		const raw = e.target.value;
		if (raw === "") {
			setWeight("");
			return;
		}

		// allow 1 decimal using . or ,
		if (/^\d*[.,]?\d?$/.test(raw)) {
			setWeight(raw.replace(",", "."));
		}
	};

	const applyPreset = (preset) => {
		setLengthCm(String(preset.dims.l));
		setWidthCm(String(preset.dims.w));
		setHeightCm(String(preset.dims.h));
	};

	const scrollToDims = () => {
		requestAnimationFrame(() => {
			dimsRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		});
	};

	const handleApplyRecommendation = () => {
		setSelectedPackageTypeId(recommendedPackageTypeId);
		setDimensionAccepted(true);
		setRecModalOpen(false);
	};

	const handleAdjustDimensions = () => {
		setRecModalOpen(false);
		scrollToDims();
	};

	const handleAddToCart = () => {
		if (!canAddToCart) return;

		/** @type {ShippingCartItem} */
		const item = {
			key: crypto.randomUUID(),
			type: "shipping",
			fromCountry,
			toCountry,
			shipmentDate,
			weightKg: Number(weight),
			billedWeightKg: roundedWeight,
			dimensionsCm: needsDimensions ? { ...dims } : undefined,
			packageTypeId: selectedPackageTypeId,
			packageTypeLabel: selectedPackageType?.label ?? "",
			priceEUR: priceResult.total,
			priceBreakdown: priceResult.breakdown,
			documentPages: isDocument ? Number(documentPages) : undefined,
			hatQuantity: isHat ? Number(hatQuantity) : undefined,
			duty: selectedPackageType?.duty,
			createdAt: new Date().toISOString(),
		};

		const signature = [
			item.fromCountry,
			item.toCountry,
			item.shipmentDate || "",
			item.weightKg.toFixed(1),
			item.packageTypeId,
			needsDimensions ? item.dimensionsCm.l : "",
			needsDimensions ? item.dimensionsCm.w : "",
			needsDimensions ? item.dimensionsCm.h : "",
			isDocument ? `pages:${documentPages || ""}` : "",
			isHat ? `pcs:${hatQuantity || ""}` : "",
		].join("|");

		const updated = [...cartItems];
		updated.push({ ...item, signature });

		setCartItems(updated);
		localStorage.setItem(CART_KEY, JSON.stringify(updated));

		// if feedback is already showing, cancel and replace immediately
		if (feedbackTimeoutRef.current !== null) {
			window.clearTimeout(feedbackTimeoutRef.current);
			feedbackTimeoutRef.current = null;
		}

		setFeedbackItem({
			fromLabel,
			toLabel,
			shipmentDate: shipmentDate ?? "-",
			packageLabel: selectedPackageType?.label ?? "-",
			billed: formatQuantityLabel(item),
			priceLabel: formatOptionalEUR(priceResult.total),
		});

		setFeedbackVisible(true);

		feedbackTimeoutRef.current = window.setTimeout(() => {
			setFeedbackVisible(false);
			setFeedbackItem(null);
			feedbackTimeoutRef.current = null;
		}, 8000);

		// reset
		setSelectedPackageTypeId("");
		setWeight("");
		setLengthCm("");
		setWidthCm("");
		setHeightCm("");
		setDocumentPages("");
		setHatQuantity("");
		setShipmentDate("");
		setProgressStep(0);
		setRecModalOpen(false);
	};

	return (
		<section
			id="shipment"
			className={[
				"py-24",
				variant === "home" ? "bg-primary" : "bg-white",
			].join(" ")}
		>
			<div className="max-w-5xl mx-auto px-4 sm:px-6">
				<div className="text-center mb-10">
					<h2
						className={[
							"text-4xl font-semibold",
							variant === "home" ? "text-white" : "text-gray-900",
						].join(" ")}
					>
						SHIPPING
					</h2>

					<p
						className={[
							"mt-2 subtext text-lg",
							variant === "home"
								? "text-gray-200"
								: "text-gray-600",
						].join(" ")}
					>
						Choose destination, then describe your shipment.
					</p>
				</div>

				{/* Route card */}
				<div className="rounded-2xl border bg-white shadow-lg overflow-hidden">
					<div className="h-1 w-full bg-gradient-to-r from-red-800 via-red-600 to-red-400" />

					<div className="p-6 border-b flex items-start justify-between gap-6">
						<div>
							<h3 className="mt-3 text-xl font-semibold text-gray-900">
								Where are we shipping?
							</h3>
						</div>
					</div>

					<div className="p-6">
						<div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-stretch">
							{/* From */}
							<div className="rounded-xl border p-4">
								<div className="text-xs text-gray-500">
									From
								</div>

								{toID ? (
									<div className="mt-4 flex items-center justify-between gap-4">
										<select
											value={fromCountry}
											onChange={(e) =>
												setFromCountry(e.target.value)
											}
											className="w-full p-3 border border-gray-300 rounded-xl input-focus"
										>
											<option value="">
												Select country
											</option>
											{countries
												.filter((c) => c.id !== "ID")
												.map((c) => (
													<option
														key={c.id}
														value={c.id}
													>
														{c.name}
													</option>
												))}
										</select>

										<span className="text-xs text-gray-500 whitespace-nowrap">
											Billing country
										</span>
									</div>
								) : (
									<div className="mt-4 flex items-center justify-between">
										<div className="font-semibold text-gray-900">
											{fromLabel}
										</div>
										<span className="text-xs text-gray-500">
											Billing country
										</span>
									</div>
								)}
							</div>

							{/* Swap */}
							<div className="relative flex items-center justify-center">
								<div className="hidden md:block absolute left-0 right-0 h-px bg-gray-200" />
								<div className="md:hidden absolute top-0 bottom-0 w-px bg-gray-200" />

								<button
									type="button"
									onClick={handleSwap}
									disabled={!toCountry}
									className={[
										"relative z-10 w-12 h-12 rounded-full border bg-white shadow-md",
										"flex items-center justify-center transition",
										toCountry
											? "hover:scale-[1.03] hover:bg-gray-50 text-gray-900"
											: "bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-not-allowed",
									].join(" ")}
									title="Swap From/To"
								>
									<FaExchangeAlt />
								</button>
							</div>

							{/* To */}
							<div className="relative rounded-2xl border bg-white p-5 shadow-sm">
								<div className="text-xs font-semibold text-gray-500">
									To
								</div>

								{fromID ? (
									<select
										value={toCountry}
										onChange={(e) =>
											setToCountry(e.target.value)
										}
										className="mt-2 w-full p-3 border border-gray-300 rounded-xl input-focus"
									>
										<option value="">
											Select destination
										</option>
										{countries
											.filter((c) => c.id !== "ID")
											.map((c) => (
												<option key={c.id} value={c.id}>
													{c.name}
												</option>
											))}
									</select>
								) : (
									<div className="mt-4 flex items-center justify-between">
										<div className="font-semibold text-gray-900">
											{toLabel}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Shipment card */}
				{canShowShipment && (
					<div ref={shipmentRef} className="mt-10">
						<div className="rounded-2xl border bg-gradient-to-b bg-secondary shadow-lg overflow-hidden">
							{/* Header */}
							<div className="p-5 sm:p-6 flex items-start justify-between gap-6">
								<div>
									<h3 className="mt-3 text-xl font-semibold text-white">
										Shipment details
									</h3>
									<div className="mt-3 inline-flex items-center gap-3 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
										<span className="subtext text-sm text-white">
											{fromLabel}
										</span>
										<span className="subtext text-sm text-white">
											→
										</span>
										<span className="subtext text-sm text-white">
											{toLabel}
										</span>
									</div>
								</div>

								{/* Keep subtle inline note (still useful) */}
								{needsDimensions &&
									recommendedPackageTypeId && (
										<div className="hidden sm:block mb-4 rounded-xl border bg-amber-50 px-4 py-3">
											<div className="text-sm font-semibold text-secondary">
												Recommendation available
											</div>
											<div className="subtext text-xs text-secondary/80 mt-1">
												Your dimensions are large for
												the entered weight. Recommended:{" "}
												<span className="font-semibold">
													{recommendedLabel}
												</span>
												.
											</div>
										</div>
									)}
							</div>

							<div className="px-4 pb-5 sm:px-6 sm:pb-6">
								<PackageTypePicker
									packageTypes={packageTypes}
									selectedPackageType={selectedPackageType}
									selectedPackageTypeId={
										selectedPackageTypeId
									}
									setSelectedPackageTypeId={
										setSelectedPackageTypeId
									}
								/>

								<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
									{/* LEFT */}
									<div className="space-y-4">
										{progressStep >= 1 && needsWeight && (
											<WeightBlock
												weight={weight}
												handleWeightChange={
													handleWeightChange
												}
												willBeRounded={willBeRounded}
												isVolume={isVolume}
											/>
										)}

										{progressStep >= 2 &&
											needsDimensions && (
												<div
													ref={dimsRef}
													className={[
														"rounded-2xl transition-all duration-200",
														!dimensionAccepted
															? "border-4 border-amber-500 bg-amber-50 ring-4 ring-amber-300/50 shadow-lg"
															: "border border-transparent",
													].join(" ")}
												>
													{!dimensionAccepted && (
														<div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-amber-200 bg-amber-100/70 rounded-t-2xl">
															<FaExclamationTriangle className="text-amber-700 text-lg shrink-0" />
															<div className="text-sm font-semibold text-amber-900">
																Change
																dimensions or
																apply
																recommended
																package
															</div>
														</div>
													)}

													<div
														className={
															!dimensionAccepted
																? "animate-pulse"
																: ""
														}
													>
														<DimensionsBlock
															lengthCm={lengthCm}
															setLengthCm={
																setLengthCm
															}
															widthCm={widthCm}
															setWidthCm={
																setWidthCm
															}
															heightCm={heightCm}
															setHeightCm={
																setHeightCm
															}
															sizePresets={
																sizePresets
															}
															applyPreset={
																applyPreset
															}
														/>
													</div>
												</div>
											)}

										{progressStep >= 1 && isDocument && (
											<DocumentsBlock
												documentPages={documentPages}
												setDocumentPages={
													setDocumentPages
												}
											/>
										)}

										{progressStep >= 1 && isHat && (
											<HatsBlock
												hatQuantity={hatQuantity}
												setHatQuantity={setHatQuantity}
											/>
										)}
									</div>

									{/* RIGHT */}
									<div className="space-y-4">
										{progressStep >=
											(needsDimensions ? 3 : 2) && (
											<Calendar
												routeKey={routeKey}
												shipmentDate={shipmentDate}
												setShipmentDate={
													setShipmentDate
												}
											/>
										)}

										<div className="rounded-2xl border bg-white p-4">
											<div className="flex items-start justify-between gap-3">
												<h4 className="font-semibold text-gray-900">
													Summary
												</h4>

												<div className="relative inline-block group text-right">
													<div className="text-xl font-bold text-gray-900 inline-flex items-center gap-2 cursor-help select-none">
														{priceLabel}
														<FaInfoCircle className="text-gray-500 text-sm" />
													</div>

													{!!priceResult.breakdown && (
														<div
															className={[
																"absolute right-0 top-full mt-2 w-72",
																"rounded-xl border bg-white shadow-lg",
																"px-3 py-2 text-left",
																"opacity-0 pointer-events-none translate-y-1",
																"group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0",
																"transition duration-150",
															].join(" ")}
															role="tooltip"
														>
															<div className="text-xs font-semibold text-gray-900">
																Price
																calculation
															</div>
															{needsWeight &&
																willBeRounded && (
																	<div className="subtext text-xs text-gray-600 mt-1">
																		{
																			roundedText
																		}
																	</div>
																)}
															<div className="subtext text-xs text-gray-700 mt-2">
																{
																	priceResult.breakdown
																}
															</div>
														</div>
													)}
												</div>
											</div>

											<div className="mt-3 grid grid-cols-2 gap-3 text-sm">
												<div className="text-gray-500">
													Route
												</div>
												<div className="font-semibold text-gray-900 text-right">
													{fromLabel} → {toLabel}
												</div>

												<div className="text-gray-500">
													Package
												</div>
												<div className="font-semibold text-gray-900 text-right">
													{selectedPackageType?.label ??
														"-"}
												</div>

												<div className="text-gray-500">
													Billed
												</div>
												<div className="font-semibold text-gray-900 text-right">
													{isDocument
														? `${documentPages || "-"} pages`
														: isHat
															? `${hatQuantity || "-"} pcs`
															: roundedWeight
																? `${roundedWeight.toFixed(1)} kg`
																: "-"}
												</div>

												<div className="text-gray-500">
													Shipment date
												</div>
												<div className="font-semibold text-gray-900 text-right">
													{formatDateToDDMMYYYY(
														shipmentDate,
													)}
												</div>
											</div>

											<div className="mt-4 flex justify-end gap-3">
												<Link
													to="/cart"
													className="button-secondary"
												>
													Go to cart
												</Link>

												<button
													type="button"
													onClick={handleAddToCart}
													disabled={!canAddToCart}
													className={[
														"button-primary font-semibold",
														canAddToCart
															? "bg-secondary hover:bg-secondary-900 text-white"
															: "bg-gray-200 text-gray-500 hover:bg-gray-200 cursor-not-allowed",
													].join(" ")}
												>
													Add to cart
													<FaArrowRight className="ml-2 text-sm" />
												</button>
											</div>
										</div>

										{feedbackVisible && feedbackItem && (
											<div className="rounded-xl border bg-green-50 px-4 py-3">
												<div className="text-sm font-semibold text-green-800">
													✓ Added to cart
												</div>

												<div className="mt-1 text-xs text-green-900/80 leading-relaxed">
													<span className="font-medium">
														{feedbackItem.fromLabel}{" "}
														→ {feedbackItem.toLabel}
													</span>
													{" • "}
													{formatDateToDDMMYYYY(
														feedbackItem.shipmentDate,
													)}
													{" • "}
													{feedbackItem.packageLabel}
													{" • "}
													{feedbackItem.billed}
													{" • "}
													<span className="font-semibold">
														{
															feedbackItem.priceLabel
														}
													</span>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Recommendation Modal */}
							{recModalOpen && (
								<div className="fixed inset-0 z-50 flex items-center justify-center px-4">
									<div
										className="absolute inset-0 bg-black/50"
										onClick={() => {
											setRecModalOpen(false);
										}}
									/>

									<div className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl border p-5">
										<div className="flex items-start justify-between gap-3">
											<div>
												<div className="text-sm font-semibold text-gray-900">
													Package type recommendation
												</div>
												<div className="subtext text-xs text-gray-600 mt-1">
													Your shipment looks light
													compared to its dimensions.
												</div>
											</div>

											<button
												type="button"
												className="text-gray-500 hover:text-gray-800"
												onClick={() => {
													setRecModalOpen(false);
												}}
												aria-label="Close"
											>
												✕
											</button>
										</div>

										<div className="mt-4 rounded-xl border bg-amber-50 px-4 py-3">
											<div className="text-sm font-semibold text-amber-900">
												Recommended: {recommendedLabel}
											</div>
											<div className="subtext text-xs text-amber-900/80 mt-1">
												Entered weight:{" "}
												<span className="font-semibold">
													{Number(weight).toFixed(1)}{" "}
													kg
												</span>
												{" • "}
												Volumetric weight:{" "}
												<span className="font-semibold">
													{volumetricWeight.toFixed(
														1,
													)}{" "}
													kg
												</span>
											</div>
										</div>

										<div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
											<button
												type="button"
												className="button-primary font-semibold text-center justify-center"
												onClick={
													handleApplyRecommendation
												}
											>
												Apply
											</button>

											<button
												type="button"
												className="button-secondary text-center justify-center"
												onClick={handleAdjustDimensions}
											>
												Edit dimensions
											</button>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</section>
	);
}
